import React from "react"
import { Phone } from "lucide-react"

import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from "src/components/ui/table"
import { Skeleton } from "src/components/ui/skeleton"
import { cn } from "src/utils/cn"

import { PairBadge } from "./PairBadge"
import { PairBars } from "./PairBars"
import { VERDICT_LABELS, scoreColor } from "../constants"

// The ranked queue. Each row is clickable to open the PAIR detail sheet.
export function QualifiedQueueTable({ leads, loading, onRowClick }) {
  if (loading) return <TableSkeleton />

  if (!leads?.length) {
    return (
      <div className="rounded-xl border border-dashed border-slate-200 bg-white py-16 text-center">
        <p className="text-sm font-medium text-slate-600">No leads in this band yet</p>
        <p className="mt-1 text-[13px] text-slate-400">
          As the engine talks to and scores leads, they'll appear here ranked by PAIR score.
        </p>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
            <TableHead className="w-[26%]">Lead</TableHead>
            <TableHead className="w-[16%]">Project</TableHead>
            <TableHead className="w-[16%]">PAIR</TableHead>
            <TableHead className="w-[8%] text-center">Score</TableHead>
            <TableHead className="w-[14%]">Band</TableHead>
            <TableHead className="w-[20%]">Verdict</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leads.map((lead) => (
            <TableRow
              key={lead.id}
              className="cursor-pointer"
              onClick={() => onRowClick?.(lead)}
            >
              <TableCell>
                <div className="font-medium text-slate-900">{lead.name}</div>
                <div className="flex items-center gap-1 text-[12px] text-slate-400">
                  <Phone className="h-3 w-3" /> {lead.phone || "—"}
                </div>
              </TableCell>
              <TableCell className="text-[13px] text-slate-600">{lead.project || "—"}</TableCell>
              <TableCell><PairBars dimensions={lead.dimensions} /></TableCell>
              <TableCell className="text-center">
                <span className={cn("text-sm font-bold tabular-nums", scoreColor(lead.composite))}>
                  {lead.composite ?? "—"}
                </span>
              </TableCell>
              <TableCell><PairBadge band={lead.band} label={lead.band_label} /></TableCell>
              <TableCell className="text-[13px] text-slate-500">
                {VERDICT_LABELS[lead.verdict] || lead.verdict || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-10 w-1/4" />
          <Skeleton className="h-4 w-1/6" />
          <Skeleton className="h-4 w-1/6" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="h-6 w-20" />
        </div>
      ))}
    </div>
  )
}
